import { createContext, useContext, useEffect, useReducer, useRef, useCallback, useMemo } from 'react';
import { cartReducer, loadCartState, saveCartState, getCartStorageKey, isItemMatch } from './cart-state.js';
import { useTenant } from '../context/TenantContext.jsx';
import { vatRateOf } from '../lib/price.js';

export const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { tenant } = useTenant();
  const currentTenantId = tenant?.id || tenant?.slug || '';
  const vatRate = vatRateOf(tenant);

  const [state, dispatch] = useReducer(
    cartReducer,
    undefined,
    () => loadCartState(typeof window !== 'undefined' ? window.localStorage : null, currentTenantId, vatRate)
  );

  const prevTenantRef = useRef(currentTenantId);

  // 1. Handle Tenant Switching
  useEffect(() => {
    if (prevTenantRef.current !== currentTenantId) {
      if (typeof window !== 'undefined') {
        const loadedState = loadCartState(window.localStorage, currentTenantId, vatRate);
        dispatch({ type: 'setTenant', tenantId: currentTenantId, nextState: loadedState });
      }
      prevTenantRef.current = currentTenantId;
    }
  }, [currentTenantId, vatRate]);

  // 2. Persist Cart State to LocalStorage on Change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      saveCartState(window.localStorage, state);
    }
  }, [state]);

  // 3. Cross-Tab Storage Event Synchronization
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorage = (event) => {
      const tenantKey = getCartStorageKey(currentTenantId);
      if (event.key === tenantKey && event.newValue) {
        try {
          const syncedState = JSON.parse(event.newValue);
          dispatch({ type: 'setTenant', tenantId: currentTenantId, nextState: syncedState });
        } catch {}
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [currentTenantId]);

  const addItem = useCallback((product, quantity = 1) => {
    dispatch({ type: 'add', product, quantity, vatRate });
  }, [vatRate]);

  const importVerifiedBom = useCallback((items, idempotencyKey, mode = 'merge') => {
    dispatch({ type: 'IMPORT_VERIFIED_BOM', items, idempotencyKey, mode, vatRate });
  }, [vatRate]);

  const updateQuantity = useCallback((skuOrId, quantity) => {
    dispatch({ type: 'quantity', sku: skuOrId, productId: skuOrId, id: skuOrId, quantity, vatRate });
  }, [vatRate]);

  const removeItem = useCallback((skuOrId) => {
    dispatch({ type: 'remove', sku: skuOrId, productId: skuOrId, id: skuOrId, vatRate });
  }, [vatRate]);

  const updateCustomer = useCallback((customer) => {
    dispatch({ type: 'customer', customer });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'clear', vatRate });
  }, [vatRate]);

  const updateProductDisplay = useCallback((productId, name) => {
    dispatch({ type: 'PRODUCT_DISPLAY_UPDATED', productId, name });
  }, []);

  const isInCart = useCallback((skuOrId) => {
    return state.items.some((item) => isItemMatch(item, skuOrId));
  }, [state.items]);

  const getItemQuantity = useCallback((skuOrId) => {
    const item = state.items.find((item) => isItemMatch(item, skuOrId));
    return item ? item.quantity : 0;
  }, [state.items]);

  const value = useMemo(() => ({
    state,
    dispatch,
    items: state.items,
    customer: state.customer,
    totalItems: state.itemCount,
    itemCount: state.itemCount,
    subtotalCents: state.subtotalCents,
    taxCents: state.taxCents,
    totalCents: state.totalCents,
    importedBomKeys: state.importedBomKeys || [],
    addItem,
    importVerifiedBom,
    updateQuantity,
    removeItem,
    updateCustomer,
    clearCart,
    updateProductDisplay,
    isInCart,
    getItemQuantity,
  }), [state, addItem, importVerifiedBom, updateQuantity, removeItem, updateCustomer, clearCart, updateProductDisplay, isInCart, getItemQuantity]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}

export function useOptionalCart() {
  return useContext(CartContext);
}

